// ── OperationsTab — 운영 탭 (구 손익관리) ───────────────────────
// 3 sub-tab:
//   📊 분석: 기존 ProfitLoss (월 손익 + AI 피드백 + PDF 출력)
//   📥 매출: 빠른 매출 입력 + 매출 캘린더
//   📤 매입: 영수증 일괄 OCR + 빠른 비용 입력 + 매입 내역
//
// 세무회계는 누적 데이터를 read 관점으로 조회. 입력은 여기서.
import { toGateway } from '../chinaMode';   // 🇨🇳 V120: 중국 모드에선 영수증 사진도 중계 주소로
import React, { useState, useMemo } from 'react';
import { useIsMobile } from '../useIsMobile';
import { useTransactions } from '../useTransactions';
import ProfitLoss from './ProfitLoss';
import RevenueCalendar from './RevenueCalendar';
import QuickRevenueEntry from './QuickRevenueEntry';
import QuickExpenseEntry from './QuickExpenseEntry';
import BatchReceiptUpload from './BatchReceiptUpload';
import ExcelImport from './ExcelImport';
import MatchReceiptsModal from './MatchReceiptsModal';
import PosImportModal from './PosImportModal';
import {
  EVIDENCE_TYPES, CATEGORY_MAJOR, CATEGORY_SUB,
  evidenceLabel, categoryLabel,
} from '../data/taxConstants';
import { L, M, MU, ym, Lv } from '../i18n/L';   // V160 중국어

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');
const fmtWon = (n) => M(n);   // V160 매장 통화 (중국 매장은 ¥)

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function OperationsTab({
  user,
  profitLoss = {},
  onProfitLossChange,
  orderHistory = [],
  receivings = [],   // V158 입고 기록 → 손익 식재료비
  categories = [],
  prices = [],
  employees = [],   // V171 인건비 사람별 — 직원 목록에서 불러오기
  canAccess,
}) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('analysis');  // 'analysis' | 'revenue' | 'expense'
  const [month, setMonth] = useState(getCurrentMonth());

  // ── 거래장부 transactions — month 기준 12개월 lazy 쿼리 (5년+ 매장 대응) ──
  const dateRange = useMemo(() => {
    const start = new Date(month + '-01');
    start.setMonth(start.getMonth() - 11);
    const startDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-01`;
    const endDate = `${month}-31`;
    return { startDate, endDate };
  }, [month]);
  const { transactions, addTransaction, updateTransaction, deleteTransaction } = useTransactions(user, {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    limitCount: 5000,
  });

  // ── 편집 모달 상태 ──
  const [editTx, setEditTx] = useState(null);

  // ── 모달 상태 ──
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [revenueModalDate, setRevenueModalDate] = useState(null);
  const [posImportOpen, setPosImportOpen] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [expenseModalDate, setExpenseModalDate] = useState(null);
  const [batchOcrOpen, setBatchOcrOpen] = useState(false);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);

  const handleRevenueSave = async (txs, options = {}) => {
    // 같은 날짜 기존 매출 거래 삭제 후 새로 저장 (중복 방지)
    const replaceIds = options.replaceIds || [];
    for (const id of replaceIds) {
      try { await deleteTransaction(id); }
      catch (e) { console.warn('replace delete failed:', e.message); }
    }
    for (const tx of txs) await addTransaction(tx);
  };
  const handleExpenseSave = async (txs) => {
    for (const tx of txs) await addTransaction(tx);
  };

  // ── 이달 매출/매입 transactions ──
  const monthIncomes = useMemo(
    () => transactions.filter(t => t?.type === 'income' && t?.date?.startsWith(month)),
    [transactions, month]
  );
  const monthExpenses = useMemo(
    () => transactions.filter(t => t?.type === 'expense' && t?.date?.startsWith(month)),
    [transactions, month]
  );
  const totalIncome = monthIncomes.reduce((s, t) => s + ((Number(t.supply) || 0) + (Number(t.vat) || 0)), 0);
  const totalExpense = monthExpenses.reduce((s, t) => s + ((Number(t.supply) || 0) + (Number(t.vat) || 0)), 0);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5' }}>
      {/* sub-tab navigation */}
      <div style={{
        display: 'flex', gap: 4, padding: isMobile ? '10px 12px 0' : '14px 20px 0',
        background: '#fff', borderBottom: '1px solid #e5e7eb',
        position: 'sticky', top: 0, zIndex: 30,
      }}>
        {[
          { id: 'analysis', label: L("📊 손익 분석"), desc: L("월별 요약 + AI 피드백") },
          { id: 'revenue',  label: L("📥 매출 입력"), desc: L("카드/현금/배달 + 메뉴별") },
          { id: 'expense',  label: L("📤 매입 입력"), desc: L("영수증 OCR + 빠른 입력") },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: isMobile ? '10px 12px' : '12px 18px',
            border: 'none', background: 'transparent',
            color: tab === t.id ? '#FF6F0F' : '#9ca3af',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: 700,
            cursor: 'pointer',
            borderBottom: tab === t.id ? '3px solid #FF6F0F' : '3px solid transparent',
            transition: 'all 0.15s',
            whiteSpace: 'nowrap',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* sub-tab content */}
      {tab === 'analysis' && (
        <ProfitLoss
          profitLoss={profitLoss}
          onProfitLossChange={onProfitLossChange}
          orderHistory={orderHistory}
          receivings={receivings}
          transactions={transactions || []}
          categories={categories || []}
          employees={employees || []}
          month={month}
          onMonthChange={setMonth}
          canAccess={canAccess}
          user={user}
        />
      )}

      {tab === 'revenue' && (
        <div style={{ padding: isMobile ? '14px' : '20px', maxWidth: 1200, margin: '0 auto' }}>
          {/* 월 선택 */}
          <MonthBar month={month} onChange={setMonth} isMobile={isMobile} />

          {/* 헤더 + CTA */}
          <div style={{
            background: '#fff', borderRadius: 14, padding: isMobile ? '16px 14px' : '20px 22px',
            marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#18181B' }}>
                  {L("💰 이달 매출 {0}", fmtWon(totalIncome))}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  {L("{0}건 거래 · 일별 캘린더 클릭하면 즉시 입력", monthIncomes.length)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => setPosImportOpen(true)} style={{
                  padding: '10px 16px', borderRadius: 9,
                  border: '1.5px solid #EA580C', background: '#fff',
                  color: '#EA580C', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}>
                  {L("📊 POS 엑셀 가져오기")}
                </button>
                <button onClick={() => { setRevenueModalDate(null); setRevenueModalOpen(true); }} style={{
                  padding: '10px 20px', borderRadius: 9,
                  border: 'none', background: '#EA580C',
                  color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer',
                  boxShadow: '0 3px 10px rgba(79,125,92,0.3)',
                }}>
                  {L("✏️ 빠른 매출 입력")}
                </button>
              </div>
            </div>
          </div>

          {/* 매출 캘린더 */}
          <RevenueCalendar
            transactions={transactions || []}
            monthKey={month}
            isMobile={isMobile}
            onClickDay={(dateStr) => { setRevenueModalDate(dateStr); setRevenueModalOpen(true); }}
            onAddRevenue={() => { setRevenueModalDate(null); setRevenueModalOpen(true); }}
          />

          {/* 메뉴별 입력 안내 */}
          <div style={{
            background: '#FAFAFA', borderRadius: 12, padding: '12px 16px',
            border: '1px solid #D4D4D8', fontSize: 12, color: '#78350f', lineHeight: 1.6,
          }}>
            💡 <strong>{L("메뉴별 입력 모드")}</strong>{L("로 매출 등록하면 메뉴 이익률 랭킹이 실제 판매 기반으로 정확해져요. 홈 대시보드 \"메뉴 이익률 랭킹\"에서 확인 가능합니다.")}
          </div>
        </div>
      )}

      {tab === 'expense' && (
        <div style={{ padding: isMobile ? '14px' : '20px', maxWidth: 1200, margin: '0 auto' }}>
          <MonthBar month={month} onChange={setMonth} isMobile={isMobile} />

          {/* 헤더 + 2 CTA */}
          <div style={{
            background: '#fff', borderRadius: 14, padding: isMobile ? '16px 14px' : '20px 22px',
            marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#EA580C' }}>
                  {L("💸 이달 매입 {0}", fmtWon(totalExpense))}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  {L("{0}건 거래 · 영수증 OCR로 빠르게 입력", monthExpenses.length)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setBatchOcrOpen(true)} style={{
                flex: 1, minWidth: 200,
                padding: '12px 18px', borderRadius: 9, border: 'none',
                background: '#EA580C', color: '#fff',
                fontSize: 14, fontWeight: 800, cursor: 'pointer',
                boxShadow: '0 3px 10px rgba(217,119,6,0.3)',
              }}>
                {L("📸 영수증 일괄 OCR 업로드")}
              </button>
              <button onClick={() => setExcelImportOpen(true)} style={{
                flex: 1, minWidth: 200,
                padding: '12px 18px', borderRadius: 9, border: 'none',
                background: '#EA580C', color: '#fff',
                fontSize: 14, fontWeight: 800, cursor: 'pointer',
                boxShadow: '0 3px 10px rgba(30,107,60,0.3)',
              }}>
                {L("📊 홈택스 엑셀 일괄 등록")}
              </button>
              <button onClick={() => setMatchOpen(true)} style={{
                flex: 1, minWidth: 200,
                padding: '12px 18px', borderRadius: 9,
                border: '1.5px solid #0369a1', background: '#fff', color: '#0369a1',
                fontSize: 14, fontWeight: 800, cursor: 'pointer',
              }}>
                {L("🔗 영수증·카드 매칭")}
              </button>
              <button onClick={() => { setExpenseModalDate(null); setExpenseModalOpen(true); }} style={{
                flex: 1, minWidth: 200,
                padding: '12px 18px', borderRadius: 9,
                border: '1.5px solid #EA580C', background: '#fff', color: '#EA580C',
                fontSize: 14, fontWeight: 800, cursor: 'pointer',
              }}>
                {L("✏️ 빠른 비용 입력")}
              </button>
            </div>
          </div>

          {/* 이달 매입 내역 */}
          <ExpenseList
            transactions={monthExpenses}
            isMobile={isMobile}
            onEdit={(tx) => setEditTx(tx)}
            onDelete={async (id) => {
              if (window.confirm(L("이 매입 내역을 삭제할까요?"))) {
                await deleteTransaction(id);
              }
            }}
          />

          {/* 안내 */}
          <div style={{
            marginTop: 14, padding: '10px 14px',
            background: '#f0f9ff', borderRadius: 10,
            fontSize: 12, color: '#0369a1', lineHeight: 1.6,
          }}>
            {L("💡 매입 내역은 거래장부에 자동 저장되며, 세무회계 탭에서 분기별/연도별로 누적 정리됩니다. 카테고리별 자동 분류, 의제매입세액 공제도 자동 계산돼요.")}
          </div>
        </div>
      )}

      {/* 모달들 */}
      <QuickRevenueEntry
        isOpen={revenueModalOpen}
        defaultDate={revenueModalDate}
        categories={categories}
        transactions={transactions || []}
        isMobile={isMobile}
        onClose={() => setRevenueModalOpen(false)}
        onSave={handleRevenueSave}
      />
      <QuickExpenseEntry
        isOpen={expenseModalOpen}
        defaultDate={expenseModalDate}
        isMobile={isMobile}
        onClose={() => setExpenseModalOpen(false)}
        onSave={handleExpenseSave}
      />
      {batchOcrOpen && (
        <BatchReceiptUpload
          user={user}
          defaultType="expense"
          prices={prices}
          existingTransactions={transactions || []}
          onSaveAll={async (txns) => {
            for (const t of txns) await addTransaction(t);
            setBatchOcrOpen(false);
          }}
          onClose={() => setBatchOcrOpen(false)}
        />
      )}
      <ExcelImport
        isOpen={excelImportOpen}
        existingTransactions={transactions || []}
        onClose={() => setExcelImportOpen(false)}
        onSave={async (txns) => {
          for (const t of txns) await addTransaction(t);
        }}
      />
      {posImportOpen && (
        <PosImportModal
          transactions={transactions || []}
          addTransaction={addTransaction}
          onClose={() => setPosImportOpen(false)}
        />
      )}
      <MatchReceiptsModal
        isOpen={matchOpen}
        user={user}
        transactions={transactions || []}
        onClose={() => setMatchOpen(false)}
      />
      {editTx && (
        <ExpenseEditModal
          tx={editTx}
          isMobile={isMobile}
          onClose={() => setEditTx(null)}
          onSave={async (patch) => {
            await updateTransaction(editTx.id, patch);
            setEditTx(null);
          }}
        />
      )}
    </div>
  );
}

// ── 월 선택 바 ──
function MonthBar({ month, onChange, isMobile }) {
  const now = new Date();
  const months = [];
  // 24개월 — 옛 데이터(POS 엑셀, 빠른 입력 등) 지원
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '10px 14px',
      marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
    }}>
      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>{L("📅 월")}</span>
      <select value={month} onChange={e => onChange(e.target.value)} style={{
        padding: '6px 10px', borderRadius: 7,
        border: '1.5px solid #e5e7eb', fontSize: 13, background: '#fff', cursor: 'pointer',
      }}>
        {months.map(m => (
          <option key={m} value={m}>{ym(m)}</option>
        ))}
      </select>
    </div>
  );
}

// ── 이달 매입 내역 리스트 ──
function ExpenseList({ transactions, isMobile, onEdit, onDelete }) {
  if (transactions.length === 0) {
    return (
      <div style={{
        background: '#fff', borderRadius: 12, padding: '28px 16px',
        textAlign: 'center', color: '#9ca3af', fontSize: 13,
        boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
      }}>
        <div style={{ fontSize: 32, marginBottom: 6 }}>📭</div>
        {L("이달 매입 내역이 없습니다 — 영수증 OCR 또는 빠른 입력으로 추가하세요")}
      </div>
    );
  }
  return (
    <div style={{
      background: '#fff', borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
    }}>
      <div style={{
        padding: '10px 16px', background: '#FAFAFA',
        fontSize: 12, fontWeight: 800, color: '#78350f',
        borderBottom: '1px solid #D4D4D8',
      }}>
        {L("이달 매입 {0}건", transactions.length)}
      </div>
      <div style={{ maxHeight: 480, overflowY: 'auto' }}>
        {transactions.map(t => {
          const amt = (Number(t.supply) || 0) + (Number(t.vat) || 0);
          const evTypeId = t.evidenceType || t.evidence?.type || null;
          const evidMeta = evTypeId ? EVIDENCE_TYPES.find(e => e.id === evTypeId) : null;
          return (
            <div key={t.id} style={{
              padding: '10px 14px',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex', alignItems: 'center', gap: 10,
              cursor: onEdit ? 'pointer' : 'default',
            }}
            onClick={(e) => {
              // 삭제 버튼 클릭은 별도 처리
              if (e.target.closest('[data-action="delete"]')) return;
              if (onEdit) onEdit(t);
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>
                    {t.date?.slice(5).replace('-', '/')}
                  </span>
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: '#1a1a1a',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {t.partnerName || t.memo || t.itemName || L("매입")}
                  </span>
                </div>
                {t.memo && t.memo !== t.partnerName && (
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.memo}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#EA580C' }}>
                  {M(amt)}
                </div>
                <EvidenceBadge meta={evidMeta} />
              </div>
              <button
                data-action="delete"
                onClick={(e) => { e.stopPropagation(); onDelete(t.id); }}
                title={L("삭제")}
                style={{
                  width: 26, height: 26, borderRadius: '50%',
                  border: '1px solid #F4F4F5', background: '#fff',
                  color: '#52525B', cursor: 'pointer', fontSize: 12,
                  flexShrink: 0,
                }}>×</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 매입 내역 편집 모달 ──
function ExpenseEditModal({ tx, isMobile, onClose, onSave }) {
  const initTotal = (Number(tx.supply) || 0) + (Number(tx.vat) || 0) + (Number(tx.taxFreeAmount) || 0);
  const [date, setDate] = useState(tx.date || '');
  const [partnerName, setPartnerName] = useState(tx.partnerName || '');
  const [amount, setAmount] = useState(String(initTotal));
  const [evidenceType, setEvidenceType] = useState(tx.evidenceType || tx.evidence?.type || '');
  const [isTaxFree, setIsTaxFree] = useState(!!(tx.taxFreeAmount && Number(tx.taxFreeAmount) > 0));
  const [catMajor, setCatMajor] = useState(tx.category?.major || '');
  const [catSub, setCatSub] = useState(tx.category?.sub || '');
  const [memo, setMemo] = useState(tx.memo || '');
  const [saving, setSaving] = useState(false);
  const [showReceiptFull, setShowReceiptFull] = useState(false);

  const subOptions = catMajor ? (CATEGORY_SUB[catMajor] || []) : [];

  // 부가세 분해 미리보기 (저장 시 동일하게 거래장부에 들어감)
  const totalNum = Math.max(0, Math.round(Number(amount) || 0));
  const isQualified = evidenceType === 'tax_invoice' || evidenceType === 'card' || evidenceType === 'cash_receipt';
  let pSupply = totalNum, pVat = 0, pTaxFree = 0;
  if (isTaxFree) {
    pTaxFree = totalNum;
    pSupply = 0;
  } else if (isQualified) {
    pSupply = Math.round(totalNum / 1.1);
    pVat = totalNum - pSupply;
  }

  // 메모/거래처에서 증빙 종류 키워드 자동 감지 (OCR이 놓친 경우 보완)
  const suggestedEvidence = (() => {
    if (evidenceType) return null;
    const text = ((partnerName || '') + ' ' + (memo || ''));
    if (/세금계산서|전자세금계산서|공급자등록번호|공급받는자/i.test(text)) {
      return { id: 'tax_invoice', label: L("세금계산서") };
    }
    if (/현금영수증|지출증빙|소득공제/i.test(text)) {
      return { id: 'cash_receipt', label: L("현금영수증") };
    }
    if (/비씨|BC카드|신한카드|하나카드|삼성카드|국민카드|KB카드|현대카드|우리카드|롯데카드|NH카드|농협카드|IBK카드|카카오뱅크|토스카드|Visa|VISA|Master|MasterCard|JCB|일시불|할부|신용카드|체크카드|매출전표|승인번호|가맹점번호/i.test(text)) {
      return { id: 'card', label: L("카드매출전표") };
    }
    return null;
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      const evidencePatch = evidenceType
        ? { type: evidenceType, isQualified: ['tax_invoice','card','cash_receipt'].includes(evidenceType) }
        : null;
      await onSave({
        date, partnerName,
        supply: pSupply,
        vat: pVat,
        taxFreeAmount: pTaxFree,
        evidenceType: evidenceType || null,
        evidence: evidencePatch,
        category: catMajor ? { major: catMajor, sub: catSub || null } : null,
        memo,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, padding: isMobile ? 16 : 22,
        width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{L("✏️ 매입 내역 수정")}</h3>
          <button onClick={onClose} style={{
            border: 'none', background: 'transparent', fontSize: 22, color: '#9ca3af', cursor: 'pointer',
          }}>×</button>
        </div>

        {/* 영수증 사진 미리보기 — 있으면 클릭하면 풀스크린 */}
        {tx.receiptImageUrl && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 }}>
              {L("📷 영수증 원본")}
            </div>
            <div
              onClick={() => setShowReceiptFull(true)}
              style={{
                position: 'relative', cursor: 'zoom-in', borderRadius: 8,
                overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb',
              }}>
              <img src={toGateway(tx.receiptImageUrl)} alt={L("영수증 원본")}
                style={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block', background: '#fff' }} />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'rgba(0,0,0,0.5)', color: '#fff',
                fontSize: 11, fontWeight: 600, padding: '4px 8px', textAlign: 'center',
              }}>
                {L("🔍 탭하면 크게 보기")}
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
              {L("세법상 5년 보관 자동 — 종이 영수증 폐기해도 안전합니다")}
            </div>
          </div>
        )}

        <Field label={L("📅 날짜")}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
        </Field>

        <Field label={L("🏢 거래처")}>
          <input value={partnerName} onChange={e => setPartnerName(e.target.value)}
                 placeholder={L("거래처명")} style={inputStyle} />
        </Field>

        <Field label={L("💰 총액 (부가세 포함)")}>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                 placeholder={MU()} style={inputStyle} />
          {/* 자동 부가세 분해 표시 */}
          {totalNum > 0 && (
            <div style={{
              marginTop: 6, padding: '8px 10px', borderRadius: 6,
              background: isTaxFree ? '#fefce8' : isQualified ? '#eff6ff' : '#F4F4F5',
              border: `1px dashed ${isTaxFree ? '#fde047' : isQualified ? '#93c5fd' : '#D4D4D8'}`,
              fontSize: 11, lineHeight: 1.6,
            }}>
              {isTaxFree ? (
                <>
                  <div style={{ color: '#854d0e' }}>
                    {L("🚫 면세 공급가액")} <strong>{M(pTaxFree)}</strong> {L("· 부가세 0원")}
                  </div>
                  <div style={{ color: '#854d0e', fontStyle: 'italic', marginTop: 2 }}>
                    {L("매입세액 공제 X (의제매입공제는 별도 검토)")}
                  </div>
                </>
              ) : isQualified ? (
                <>
                  <div style={{ color: '#0369a1' }}>
                    {L("공급가액")} <strong>{M(pSupply)}</strong> {L("+ 부가세")} <strong style={{ color: '#18181B' }}>{M(pVat)}</strong> {L("= {0}", M(totalNum))}
                  </div>
                  <div style={{ color: '#18181B', fontStyle: 'italic', marginTop: 2 }}>
                    {L("✓ 매입세액 공제 가능 (부가세 신고 시 자동 반영)")}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ color: '#52525B' }}>
                    {L("공급가액")} <strong>{M(totalNum)}</strong> {L("· 부가세 분리 안 함")}
                  </div>
                  <div style={{ color: '#52525B', fontStyle: 'italic', marginTop: 2 }}>
                    {L("⚠ 비적격증빙 — 매입세액 공제 불가")}
                  </div>
                </>
              )}
            </div>
          )}
        </Field>

        {/* 자동 추천 배너 — OCR이 evidenceType 놓쳤을 때 한 번 클릭으로 적용 */}
        {suggestedEvidence && (
          <div style={{
            marginBottom: 12, padding: '10px 12px', borderRadius: 8,
            background: '#eff6ff', border: '1px dashed #93c5fd',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 12, color: '#0369a1', lineHeight: 1.4 }}>
              {L("💡 메모에서")} <strong>{suggestedEvidence.label}</strong> {L("키워드 감지 — 자동 분류 추천")}
            </span>
            <button type="button" onClick={() => setEvidenceType(suggestedEvidence.id)}
              style={{
                padding: '6px 12px', borderRadius: 6, border: 'none',
                background: '#FF6F0F', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}>
              {L("✓ {0}로 적용", suggestedEvidence.label)}
            </button>
          </div>
        )}

                <Field label={L("🧾 증빙 종류")}>
          {/* 빠른 분류 칩 — 한 번 터치로 변경 */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap' }}>
            {[
              { id: 'card',          label: L("💳 카드매출전표"),  qualified: true },
              { id: 'tax_invoice',   label: L("📋 세금계산서"),    qualified: true },
              { id: 'cash_receipt',  label: L("🧾 현금영수증"),    qualified: true },
              { id: 'simple_receipt',label: L("✏️ 간이영수증"),    qualified: false },
            ].map(opt => {
              const active = evidenceType === opt.id;
              return (
                <button key={opt.id} type="button"
                  onClick={() => setEvidenceType(opt.id)}
                  style={{
                    flex: '1 1 calc(50% - 4px)', minWidth: 'calc(50% - 4px)',
                    padding: '8px 10px', borderRadius: 7,
                    border: active ? `1.5px solid ${opt.qualified ? '#FF6F0F' : '#52525B'}` : '1px solid #e5e7eb',
                    background: active ? (opt.qualified ? '#F4F4F5' : '#F4F4F5') : '#fff',
                    color: active ? (opt.qualified ? '#9A3412' : '#52525B') : '#6b7280',
                    fontSize: 12, fontWeight: active ? 800 : 600, cursor: 'pointer',
                  }}>
                  {opt.label}{opt.qualified ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
          <select value={evidenceType} onChange={e => setEvidenceType(e.target.value)} style={inputStyle}>
            <option value="">{L("선택 안 함")}</option>
            {EVIDENCE_TYPES.map(e => (
              <option key={e.id} value={e.id}>{Lv(e.label)}{e.qualified ? ' ✓' : ''}</option>
            ))}
          </select>
        </Field>

        <Field label={L("🌱 면세 여부")}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px', borderRadius: 6,
            background: isTaxFree ? '#fefce8' : '#f9fafb',
            border: `1px solid ${isTaxFree ? '#fde047' : '#e5e7eb'}`,
            cursor: 'pointer',
          }}>
            <input type="checkbox" checked={isTaxFree}
              onChange={e => setIsTaxFree(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <span style={{ fontSize: 12, color: isTaxFree ? '#854d0e' : '#6b7280', fontWeight: 700 }}>
              {L("면세 항목 (농수축산물·면세상품) — 부가세 0원")}
            </span>
          </label>
        </Field>

        <Field label={L("📂 카테고리")}>
          <div style={{ display: 'flex', gap: 6 }}>
            <select value={catMajor} onChange={e => { setCatMajor(e.target.value); setCatSub(''); }} style={{ ...inputStyle, flex: 1 }}>
              <option value="">{L("대분류")}</option>
              {CATEGORY_MAJOR.map(m => <option key={m.id} value={m.id}>{Lv(m.label)}</option>)}
            </select>
            <select value={catSub} onChange={e => setCatSub(e.target.value)} disabled={!catMajor} style={{ ...inputStyle, flex: 1 }}>
              <option value="">{L("소분류")}</option>
              {subOptions.map(s => <option key={s.id} value={s.id}>{Lv(s.label)}</option>)}
            </select>
          </div>
        </Field>

        <Field label={L("📝 메모")}>
          <textarea value={memo} onChange={e => setMemo(e.target.value)}
                    rows={2} placeholder={L("추가 메모...")}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
        </Field>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} disabled={saving} style={{
            flex: 1, padding: '12px', borderRadius: 10,
            border: '1.5px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}>{L("취소")}</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 2, padding: '12px', borderRadius: 10, border: 'none',
            background: saving ? '#9ca3af' : '#FF6F0F',
            color: '#fff', fontWeight: 800, fontSize: 14, cursor: saving ? 'wait' : 'pointer',
          }}>{saving ? L("저장 중…") : L("저장")}</button>
        </div>
      </div>

      {/* 영수증 풀스크린 라이트박스 */}
      {showReceiptFull && tx.receiptImageUrl && (
        <div
          onClick={() => setShowReceiptFull(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: 12, cursor: 'zoom-out',
          }}>
          <img src={toGateway(tx.receiptImageUrl)} alt={L("영수증 원본 (확대)")}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          <button onClick={() => setShowReceiptFull(false)}
            style={{
              position: 'absolute', top: 12, right: 12,
              border: 'none', background: 'rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 24, width: 40, height: 40, borderRadius: '50%',
              cursor: 'pointer',
            }}>×</button>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1.5px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  );
}

// ── 증빙 종류 배지 (적격/비적격/없음 색상 구분) ──
function EvidenceBadge({ meta }) {
  if (!meta) {
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5,
        background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb',
        whiteSpace: 'nowrap',
      }}>{L("증빙 미지정")}</span>
    );
  }
  // 색상 매핑
  let bg = '#f3f4f6', fg = '#6b7280', border = '#e5e7eb', icon = '';
  if (meta.qualified) {
    bg = '#F4F4F5'; fg = '#9A3412'; border = '#FED7AA'; icon = '✓ ';
  } else if (meta.id === 'none') {
    bg = '#F4F4F5'; fg = '#52525B'; border = '#fca5a5'; icon = '✗ ';
  } else {
    // 간이영수증, 일반영수증 — 주황 (가산세 위험)
    bg = '#F4F4F5'; fg = '#52525B'; border = '#D4D4D8'; icon = '⚠ ';
  }
  return (
    <span title={meta.desc || ''} style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5,
      background: bg, color: fg, border: `1px solid ${border}`,
      whiteSpace: 'nowrap',
    }}>{icon}{Lv(meta.label)}</span>
  );
}
